import type { UrlObject } from 'url';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { setSubpathLocale } from '@core/external-links/redux';
import { addLocaleToRelativeUrl } from '@core/internationalize/addCurrentLocaleToUrl';
import { Locale, toLocaleFromPath } from '@core/internationalize/models/locale';
import { LOCALE_TOGGLE } from '@core/internationalize/models/toggle';
import { useReduxAction } from '@engage/redux/hooks';

const hasDynamicSegments = (pathname: string) => /\[.*\]/.test(pathname);

const withTrailingSlash = (p: string) => (p.endsWith('/') ? p : `${p}/`);

type NavOptions = { scroll?: boolean; shallow?: boolean; locale?: string };

/**
 * Prepend the current subpath locale to a relative URL.
 *
 * For string URLs:  '/classes'              → '/de-DE/classes'
 * For UrlObjects:  { pathname: '/classes' } → { pathname: '/de-DE/classes' }
 *
 * Returns the URL unchanged when:
 *  - LOCALE_TOGGLE is disabled
 *  - The URL is non-relative (doesn't start with '/')
 *  - The URL is query-only ('?…') or hash-only ('#…')
 *  - The UrlObject has no pathname, or a pathname starting with '?'
 *  - The pathname contains dynamic route segments like [id] or [...slug]
 *    (Next.js needs the exact pattern to resolve its page directory)
 *  - The URL is already prefixed with the current locale
 *  - The active locale is the default (en-US)
 *
 * The `locale` param is optional — when omitted the locale is read from
 * window.location.pathname, which is the correct source of truth for
 * members app's static-export subpath routing.
 *
 * This function is idempotent — calling it on an already-locale-prefixed URL
 * is safe.
 */
export const localizeUrl = (
  url: UrlObject | string,
  locale?: Locale,
): UrlObject | string => {
  if (!LOCALE_TOGGLE) {
    return url;
  }

  const activeLocale =
    locale ??
    toLocaleFromPath(
      typeof window !== 'undefined' ? window.location.pathname : undefined,
    );

  if (typeof url === 'string') {
    if (!url.startsWith('/')) {
      return url;
    }
    return addLocaleToRelativeUrl(url, activeLocale);
  }

  // Don't localize dynamic route patterns — Next.js needs the exact template.
  if (url.pathname?.startsWith('/') && !hasDynamicSegments(url.pathname)) {
    return { ...url, pathname: addLocaleToRelativeUrl(url.pathname, activeLocale) };
  }

  return url;
};

/**
 * Convert a URLSearchParams string (e.g. "?modal=foo&ids=1&ids=2") into a
 * plain Record, collapsing duplicate keys into arrays.
 */
const parseSearchParams = (searchKey: string): Record<string, string | string[]> => {
  if (typeof window === 'undefined') {
    return {};
  }

  const result: Record<string, string | string[]> = {};
  new URLSearchParams(searchKey).forEach((value, key) => {
    if (result[key] !== undefined) {
      result[key] = Array.isArray(result[key])
        ? [...(result[key] as string[]), value]
        : [result[key] as string, value];
    } else {
      result[key] = value;
    }
  });
  return result;
};

type ParseDynamicParamsInput = {
  routerPathname: string;
  routerQuery: NextRouter['query'];
  asPathWithoutLocale: string;
  searchParams: Record<string, string | string[]>;
};

/**
 * Derive the final `query` object exposed by useMembersRouter.
 *
 * In Next.js static-export mode, router.query is often stale or contains
 * template strings (e.g. "[memberId]") because the router can't match
 * locale-prefixed URLs against page route patterns.  We rebuild dynamic params
 * by positionally matching window.location.pathname against the route template.
 */
const parseDynamicParams = ({
  routerPathname,
  routerQuery,
  asPathWithoutLocale,
  searchParams,
}: ParseDynamicParamsInput): Record<string, string | string[] | undefined> => {
  // Guard for tests/SSR where router.pathname may not be set yet.
  if (!routerPathname) {
    return { ...searchParams };
  }

  // Detect catch-all ([...slug]) and optional-catch-all ([[...slug]]) segments.
  const catchAllParam = routerPathname.match(/\[{1,2}\.\.\.(\w+)\]{1,2}/)?.[1];

  // All single [param] names (excludes catch-all).
  const singleDynamicParams = [...routerPathname.matchAll(/\[(\w+)\]/g)].map(m => m[1]);

  const allDynamicParamNames = catchAllParam ? [catchAllParam] : singleDynamicParams;

  const isTemplateString = (v: unknown): boolean =>
    typeof v === 'string' && v.startsWith('[') && v.endsWith(']');

  // If Next.js has already resolved all dynamic params with real values, use them.
  const allPopulated =
    allDynamicParamNames.length > 0 &&
    allDynamicParamNames.every(
      p => routerQuery[p] !== undefined && !isTemplateString(routerQuery[p]),
    );

  if (allPopulated) {
    // Normalize catch-all value to an array when Next.js returns a plain string.
    if (catchAllParam && !Array.isArray(routerQuery[catchAllParam])) {
      return {
        ...searchParams,
        ...routerQuery,
        [catchAllParam]: String(routerQuery[catchAllParam]).split('/'),
      };
    }
    return { ...searchParams, ...routerQuery };
  }

  // No dynamic segments — merge routerQuery into searchParams.
  //
  // All navigation in the members app goes through customPush/customReplace, which pass a
  // resolved { pathname, query } object to Next.js. This means router.query is always
  // updated synchronously before routeChangeComplete fires, regardless of locale prefix.
  // Merging routerQuery here gives components like LegacyConnectedModal immediate access
  // to modal params on push — matching the behaviour of allPopulated (dynamic-param) pages.
  if (allDynamicParamNames.length === 0) {
    return { ...searchParams, ...routerQuery };
  }

  const actualPathClean = asPathWithoutLocale.split('?')[0];

  // Catch-all: collect every segment after the static base path.
  if (catchAllParam) {
    const basePath = routerPathname.split('/[')[0];
    if (!actualPathClean.startsWith(basePath)) {
      return { ...routerQuery, ...searchParams };
    }
    const segments = actualPathClean
      .slice(basePath.length)
      .replace(/^\//, '')
      .split('/')
      .filter(Boolean);
    if (segments.length === 0) {
      return { ...routerQuery, ...searchParams };
    }
    return { ...searchParams, ...routerQuery, [catchAllParam]: segments };
  }

  // Single params: match each [paramName] to its positional URL segment.
  //   Pattern: /members/[memberId]/workouts/[workoutId]
  //   Actual:  /members/0e22b.../workouts/3ab0d...
  const urlSegments = routerPathname.split('/').filter(Boolean);
  const actualUrlSegments = actualPathClean.split('/').filter(Boolean);

  if (actualUrlSegments.length < urlSegments.length) {
    return { ...routerQuery, ...searchParams };
  }

  const dynamicParams: Record<string, string> = {};
  urlSegments.forEach((seg, i) => {
    const match = seg.match(/^\[(\w+)\]$/);
    if (match) {
      dynamicParams[match[1]] = decodeURIComponent(actualUrlSegments[i] ?? '');
    }
  });

  return { ...searchParams, ...routerQuery, ...dynamicParams };
};

/**
 * Shared preparation for router.push / router.replace.
 *
 * - Query-only navigations (opening/closing a modal, applying filters — a push
 *   with a `query` but no `pathname`) stay on the current page. For these we set
 *   the route `url` to the ROUTE TEMPLATE (e.g. /classes/[[...categorySlug]]) so
 *   Next can resolve the page, and we flag `isQueryOnlyNav` so the caller can
 *   navigate shallowly. Pushing a concrete path (/classes/all/) instead forces a
 *   hard navigation on locale-prefixed pages: the localized `as`
 *   (/de-DE/classes/all/) matches no static-export page bundle, so Next falls
 *   back to `window.location = as` and reloads the whole page.
 * - Localizes only the display URL (`as`), never the route URL — localizing the
 *   route would produce '/de-DE/feed' which has no matching page and triggers a
 *   hard navigation that drops the locale.
 * - Promotes a query-only `as` string (e.g. '?modal=foo') to a full UrlObject
 *   so the trailing slash is preserved and Cloudflare's redirect is never hit.
 */
const buildUseRouterArgs = (
  url: UrlObject | string,
  as: UrlObject | string | undefined,
  currentPathname: string,
  locale: Locale | undefined,
  routePathname: string,
): {
  normalizedUrl: UrlObject | string;
  effectiveAs: UrlObject | string;
  isQueryOnlyNav: boolean;
} => {
  const isQueryOnlyNav = typeof url === 'object' && !!url.query && !url.pathname;

  const normalizedUrl = isQueryOnlyNav
    ? { ...(url as UrlObject), pathname: routePathname }
    : url;

  let effectiveAs: UrlObject | string;
  if (as !== undefined) {
    effectiveAs =
      typeof as === 'string' && as.startsWith('?')
        ? localizeUrl({ pathname: currentPathname, search: as }, locale)
        : localizeUrl(as, locale);
  } else if (isQueryOnlyNav) {
    // Display the localized CONCRETE current path (not the template).
    effectiveAs = localizeUrl(
      { ...(url as UrlObject), pathname: currentPathname },
      locale,
    );
  } else {
    effectiveAs = localizeUrl(normalizedUrl, locale);
  }

  return { normalizedUrl, effectiveAs, isQueryOnlyNav };
};

export const useMembersRouter = () => {
  const router = useRouter();
  const dispatchSetSubpathLocale = useReduxAction(setSubpathLocale);

  // ── searchKey ───────────────────────────────────────────────────────────────
  // Eagerly-updated mirror of window.location.search.  Kept in state so React
  // re-renders when the URL changes.  Updated synchronously in push/replace so
  // query params reflect the intended state *before* routeChangeComplete fires
  // (avoids a window where stale params like ?modal=foo linger after close).
  const [searchKey, setSearchKey] = useState(
    typeof window !== 'undefined' ? window.location.search : '',
  );

  useEffect(() => {
    const handleRouteChange = () => setSearchKey(window.location.search);
    router.events?.on('routeChangeComplete', handleRouteChange);
    return () => router.events?.off('routeChangeComplete', handleRouteChange);
  }, [router.events]);

  // In static-export mode router.asPath returns the route template (e.g.
  // /collections/[collectionId]) on dynamic pages.  Use window.location.pathname
  // as the source of truth for those cases.
  const actualPath = useMemo(() => {
    if (typeof window === 'undefined') {
      return router.asPath;
    }
    return hasDynamicSegments(router.asPath) ? window.location.pathname : router.asPath;
  }, [router.asPath]);

  const locale = useMemo(() => toLocaleFromPath(actualPath), [actualPath]);

  useEffect(() => {
    if (!LOCALE_TOGGLE) {
      return;
    }
    dispatchSetSubpathLocale(locale !== Locale.EnglishUnitedStates ? locale : undefined);
  }, [locale, dispatchSetSubpathLocale]);

  const asPathWithoutLocale = useMemo(() => {
    if (!locale) {
      return actualPath;
    }
    const localePrefix = `/${locale}`;
    return actualPath.startsWith(localePrefix)
      ? actualPath.slice(localePrefix.length) || '/'
      : actualPath;
  }, [actualPath, locale]);

  const searchParams = useMemo(() => parseSearchParams(searchKey), [searchKey]);

  const query = useMemo(
    () =>
      parseDynamicParams({
        routerPathname: router.pathname,
        routerQuery: router.query,
        asPathWithoutLocale,
        searchParams,
      }),
    [router.query, router.pathname, asPathWithoutLocale, searchParams],
  );

  const currentPathname = useMemo(
    () => withTrailingSlash(asPathWithoutLocale.split('?')[0]),
    [asPathWithoutLocale],
  );

  // Synchronously snapshot the intended search string from push/replace so that
  // `searchParams` (and therefore `query`) updates before routeChangeComplete.
  const eagerlyUpdateSearchKey = useCallback((normalizedUrl: UrlObject | string) => {
    if (typeof normalizedUrl !== 'object' || normalizedUrl.query === undefined) {
      return;
    }

    const params = new URLSearchParams();
    Object.entries(normalizedUrl.query as Record<string, string | string[]>).forEach(
      ([k, v]) => {
        if (v === undefined || v === null) {
          return;
        }
        if (Array.isArray(v)) {
          v.forEach(item => params.append(k, item));
        } else {
          params.set(k, String(v));
        }
      },
    );

    const search = params.toString();
    setSearchKey(search ? `?${search}` : '');
  }, []);

  const customPush = useCallback(
    (url: UrlObject | string, as?: UrlObject | string, options?: NavOptions) => {
      const { normalizedUrl, effectiveAs, isQueryOnlyNav } = buildUseRouterArgs(
        url,
        as,
        currentPathname,
        locale,
        router.pathname,
      );
      eagerlyUpdateSearchKey(normalizedUrl);

      return router.push(
        normalizedUrl,
        effectiveAs,
        isQueryOnlyNav ? { ...options, shallow: true } : options,
      );
    },
    [router, locale, currentPathname, eagerlyUpdateSearchKey],
  );

  const customReplace = useCallback(
    (url: UrlObject | string, as?: UrlObject | string, options?: NavOptions) => {
      const { normalizedUrl, effectiveAs, isQueryOnlyNav } = buildUseRouterArgs(
        url,
        as,
        currentPathname,
        locale,
        router.pathname,
      );
      eagerlyUpdateSearchKey(normalizedUrl);

      return router.replace(
        normalizedUrl,
        effectiveAs,
        isQueryOnlyNav ? { ...options, shallow: true } : options,
      );
    },
    [router, locale, currentPathname, eagerlyUpdateSearchKey],
  );

  const isReady = typeof window !== 'undefined' || router.isReady;

  return {
    ...router,
    asPath: asPathWithoutLocale,
    query,
    locale,
    push: customPush,
    replace: customReplace,
    isReady,
  } as NextRouter;
};
